import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  useBrandsQuery,
  useCreateBrandMutation,
  useDeleteServiceProductChildMutation,
  useIconsQuery,
  useProductTypeQuery,
  useServiceProductsQuery,
} from "../../Store/Apis/servicesApi";
import IconSelector from "../IconSelector/IconSelector";
import toast from "react-hot-toast";
import AddEditBrand from "../AddEditBrand/AddEditBrand";
import { useSelector } from "react-redux";
import { getAllClients } from "../../Store/Apis/ReservationApi";

const AddEditServiceProduct = ({ onSubmit, data }) => {
  const user = useSelector((state) => state.auth.user);
  const [serviceTypeFilter, setServiceTypeFilter] = useState("");
  const serviceTypesApi = useProductTypeQuery({
    outletID: serviceTypeFilter,
  });
  const [outletFilter, setOutletFilter] = useState("");
  const brandsApi = useBrandsQuery({
    outletID: outletFilter,
  });

  // Fetch all existing products/services to determine already-allocated specifications
  const allProductsApi = useServiceProductsQuery({
    outletID: serviceTypeFilter,
  });

  // Compute set of already-allocated specification type IDs (excluding current product's own)
  const allocatedTypeIds = React.useMemo(() => {
    const allocated = new Set();
    if (allProductsApi?.data?.data) {
      allProductsApi.data.data.forEach((parent) => {
        // Skip the current product being edited
        if (data && parent.parentId === data.parentId) return;
        parent.childRecords?.forEach((child) => {
          if (child.typeId) {
            allocated.add(Number(child.typeId));
          }
        });
      });
    }
    return allocated;
  }, [allProductsApi?.data?.data, data]);
  const [form, setForm] = useState({
    type: "product",
    brand: "",
    name: "",
    description: "",
    restock: "",
    icon: null,
    iconString: "",
    productDetails: [],
    isActive: true,
  });

  // Track used codes to ensure uniqueness
  const [usedCodes, setUsedCodes] = useState(new Set());

  // Function to generate unique code
  const generateUniqueCode = (type) => {
    const outletID = user.outletID || "001";
    const typePrefix = type === "service" || type === 2 ? "SER" : "PRO";

    let code;
    let attempts = 0;
    const maxAttempts = 1000; // Prevent infinite loop

    do {
      const randomNum = Math.floor(1000 + Math.random() * 9000); // 4 digit number (1000-9999)
      code = `${outletID}${typePrefix}${randomNum}`;
      attempts++;
    } while (usedCodes.has(code) && attempts < maxAttempts);

    if (attempts >= maxAttempts) {
      // Fallback: use timestamp
      const timestamp = Date.now().toString().slice(-4);
      code = `${outletID}${typePrefix}${timestamp}`;
    }

    setUsedCodes((prev) => new Set([...prev, code]));
    return code;
  };

  const [errors, setErrors] = useState({});
  const [showIconModal, setShowIconModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  // Company search (optional) - per item row
  const [companyQueries, setCompanyQueries] = useState({}); // index -> query
  const [companyResultsMap, setCompanyResultsMap] = useState({}); // index -> results array
  const [companyLoadingMap, setCompanyLoadingMap] = useState({});
  const [selectedCompanies, setSelectedCompanies] = useState({});
  const companySearchTimers = useRef({});

  // Validation rules
  const validate = () => {
    const newErrors = {};

    if (!form.type) {
      newErrors.type = "Type is required";
    }
    if (!form.name.trim()) {
      newErrors.name = "Product/Service Name is required";
    }
    if (form.description && form.description.length < 10) {
      newErrors.description = "Description must be at least 10 characters long";
    }

    console.log(form.type);

    form.productDetails.forEach((detail, index) => {
      const isProduct = form.type === "product" || form.type === 1;
      const reOrderLevel = parseFloat(detail.reOrderLevel);
      const rate = parseFloat(detail.rate);
      const quantity = parseFloat(detail.quantity);
      const costPrice = parseFloat(detail.costPrice);

      if (isProduct && (Number.isNaN(reOrderLevel) || reOrderLevel < 0)) {
        newErrors[`detail-reOrderLevel-${index}`] =
          "Re-Order Level must be 0 or more";
      }
      if (Number.isNaN(rate) || rate <= 0) {
        newErrors[`detail-rate-${index}`] = "Rate must be greater than 0";
      }
      if (!detail.type) {
        newErrors[`detail-type-${index}`] = "Product/Service Type is required";
      }
      if (isProduct && (Number.isNaN(quantity) || quantity <= 0)) {
        newErrors[`detail-quantity-${index}`] =
          "Quantity must be greater than 0";
      }
      if (isProduct && (Number.isNaN(costPrice) || costPrice <= 0)) {
        newErrors[`detail-costPrice-${index}`] =
          "Cost Price must be greater than 0";
      }
      if (!detail.code || !detail.code.trim()) {
        newErrors[`detail-code-${index}`] = "Code is required";
      }
    });

    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleProductDetailsChange = (index, e) => {
    const { name, value } = e.target;
    const updatedDetails = [...form.productDetails];

    // Keep raw value for inputs so users can clear the field (no forced 0)
    updatedDetails[index] = {
      ...updatedDetails[index],
      [name]: value,
    };

    // Optional: calculate total if needed (depends on your structure)
    if (name === "quantity" || name === "costPrice" || name === "price") {
      const quantity = parseFloat(updatedDetails[index].quantity);
      const costPrice = parseFloat(updatedDetails[index].costPrice);
      const price = parseFloat(updatedDetails[index].price);
      const q = Number.isNaN(quantity) ? 0 : quantity;
      const p = Number.isNaN(price) ? 0 : price;
      updatedDetails[index].total = (q * p).toFixed(2);
    }

    setForm({ ...form, productDetails: updatedDetails });
  };

  const addMoreRows = () => {
    const newCode = generateUniqueCode(form.type);
    setForm({
      ...form,
      productDetails: [
        ...form.productDetails,
        {
          quantity: "",
          costPrice: "",
          price: "",
          reOrderLevel: "",
          rate: "",
          icon: null,
          code: newCode, // Auto-generate code
        },
      ],
    });
  };

  const handleDeleteRow = (index) => {
    setRowToDelete(index);
    setShowDeleteModal(true);
  };

  const confirmDeleteRow = () => {
    const updatedDetails = form.productDetails.filter(
      (_, i) => i !== rowToDelete,
    );
    setForm({ ...form, productDetails: updatedDetails });
    setShowDeleteModal(false);
  };

  const handleIconSelect = (icon) => {
    setForm({ ...form, icon: icon.id, iconString: icon.image });
    setShowIconModal(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formErrors = validate();
    if (Object.keys(formErrors).length === 0) {
      console.log(form);
      // Create a filtered form object based on the type
      const filteredForm =
        form.type === "service"
          ? {
              outletID: user.outletID,
              type: form.type,
              brandId: -1,
              name: form.name,
              isActive: form.isActive,
              iconID: form.icon,
              serviceDetails: form.productDetails.map((detail, idx) => ({
                childId: detail.childId,
                serviceCode: detail.code,
                serviceTypeId: Number(detail.type),
                rate: Number(detail.rate),
                icon: detail.icon || "",
                description: detail.description,
                ...(selectedCompanies[idx]?.id
                  ? { CompanyID: selectedCompanies[idx].id }
                  : {}),
              })),
            }
          : {
              outletID: user.outletID,
              type: form.type,
              brandId: Number(form.brand),
              name: form.name,
              isActive: form.isActive,
              iconID: form.icon,
              productDetails: form.productDetails.map((detail, idx) => ({
                childId: detail.childId,
                productCode: detail.code,
                productTypeId: Number(detail.type),
                reorderLevel: Number(detail.reOrderLevel),
                rate: Number(detail.rate),
                costPrice: Number(detail.costPrice),
                quantity: Number(detail.quantity),
                price: detail.price,
                icon: detail.icon || "",
                description: detail.description,
                ...(detail.CompanyID
                  ? { CompanyID: detail.CompanyID }
                  : selectedCompanies[idx]?.id
                    ? { CompanyID: selectedCompanies[idx].id }
                    : {}),
              })),
            };
      onSubmit(filteredForm);
    } else {
      setErrors(formErrors);
    }
  };

  // ADD BRAND COMPONENT
  const [createBrand, createBrandResponse] = useCreateBrandMutation();
  const [addBrandModalShow, setAddBrandModalShow] = useState(false);
  const AddBrand = () => {
    // const [createOutlet, createOutletResponse] = useCreateOutletMutation()
    const handleCreateBrandSubmit = (formData) => {
      console.log("Brand Submitted:", formData);
      const brandBody = {
        brandName: formData.brandName,
        categoryId: formData.category,
        image: formData.logo,
        isActive: 0,
      };
      createBrand(brandBody)
        .then((res) => {
          console.log(res);
          if (res?.data?.status == 200) {
            setAddBrandModalShow(false);
            toast.success(res?.data?.message, { duration: 7000 });
          } else {
            toast.error(res?.data?.message, { duration: 7000 });
          }
        })
        .catch((err) => {
          toast.error(err, { duration: 7000 });
        });
    };

    return (
      <Modal
        show={addBrandModalShow}
        onHide={() => setAddBrandModalShow(false)}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Brand</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AddEditBrand onSubmit={handleCreateBrandSubmit} />
        </Modal.Body>
      </Modal>
    );
  };

  // HANDLE DELETE
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [deleteServiceProductChild] = useDeleteServiceProductChildMutation();
  const handleDeleteServiceProductChild = (props) => {
    console.log("Selected record for deletion:", selectedRecord);
    console.log(
      "Available fields in selectedRecord:",
      Object.keys(selectedRecord || {}),
    );

    // Try multiple possible field names for the ID
    const possibleIds = [
      selectedRecord?.childId,
      selectedRecord?.ChildId,
      selectedRecord?.id,
      selectedRecord?.ID,
      selectedRecord?.keyId,
      selectedRecord?.keyID,
    ];

    const validId = possibleIds.find(
      (id) => id !== undefined && id !== null && id !== "",
    );

    if (!selectedRecord) {
      console.error("No selected record found");
      toast.error("Unable to delete: No item selected", { duration: 5000 });
      setSelectedRecord(null);
      return;
    }

    // If no valid ID is found, this is a new UI row that hasn't been saved yet
    // Just remove from local state without API call
    if (!validId) {
      console.log(
        "No valid ID found - treating as new UI row, removing from local state only",
      );
      const updatedDetails = form.productDetails.filter(
        (detail) => detail !== selectedRecord,
      );
      setForm({ ...form, productDetails: updatedDetails });
      setSelectedRecord(null);
      toast.success("Item removed successfully", { duration: 3000 });
      return;
    }

    console.log("Using ID for deletion:", validId);

    // This is an existing row with an ID - call the API to delete it
    deleteServiceProductChild({
      type: data.type,
      id: validId,
    })
      .then((res) => {
        console.log("Delete response:", res);
        if (res.data && res.data.status == 200) {
          toast.success("Item deleted successfully", { duration: 3000 });
          setSelectedRecord(null);
          // Refresh the product details by removing the deleted item from the state
          const updatedDetails = form.productDetails.filter(
            (detail) =>
              detail.childId !== validId && detail.ChildId !== validId,
          );
          setForm({ ...form, productDetails: updatedDetails });
        } else {
          const errorMessage =
            res.data?.message ||
            res.error?.data?.message ||
            "Failed to delete item";
          toast.error(errorMessage, { duration: 5000 });
        }
      })
      .catch((error) => {
        console.error("Delete error:", error);
        const errorMessage =
          error?.data?.message ||
          error.message ||
          "An error occurred while deleting";
        toast.error(errorMessage, { duration: 5000 });
      });
  };

  useEffect(() => {
    if (data) {
      // Extract existing codes to prevent duplicates
      const existingCodes = data.childRecords
        .map((elem) => elem.code)
        .filter(Boolean);
      setUsedCodes(new Set(existingCodes));

      setForm({
        type: data.type === 1 ? "product" : "service",
        name: data.productServiceName,
        brand: data.brandID,
        description: "",
        restock: "",
        icon: data.iconID,
        iconString: data.iconImage,
        productDetails: data.childRecords.map((elem, i) => {
          console.log("Mapping child record:", elem); // Debug log
          const clientName =
            elem.clientName || elem.companyName || elem.customerName || "";
          const companyID =
            elem.CompanyID || elem.CompanyId || elem.companyID || null;

          // Try multiple possible field names for childId to handle case sensitivity
          const childId = elem.childId || elem.ChildId || elem.id || elem.ID;
          const typeId = elem.typeId || elem.TypeId || elem.type_id;

          if (data.type === 1) {
            //IF ITS A PRODUCT
            return {
              childId: childId,
              ChildId: childId, // Keep both for compatibility
              code: elem.code,
              description: elem.description,
              rate: `${elem.rate}`,
              icon: elem.icon,
              type: typeId,
              reOrderLevel: `${elem.reorderLevel || elem.ReorderLevel}`,
              quantity: `${elem.quantity}`,
              costPrice: `${elem.costPrice}`,
              price: `${elem.rate}`,
              clientName,
              ...(companyID ? { CompanyID: companyID } : {}),
            };
          } else {
            //IF ITS A SERVICE
            return {
              childId: childId,
              ChildId: childId, // Keep both for compatibility
              code: elem.code,
              description: elem.description,
              rate: `${elem.rate}`,
              icon: elem.icon,
              type: typeId,
              clientName,
              ...(companyID ? { CompanyID: companyID } : {}),
            };
          }
        }),
        isActive: data.isActive,
      });
    }
  }, [data]);

  useEffect(() => {
    if (user.role !== "superAdmin") {
      setOutletFilter(user.outletID);
    }
  }, []);

  useEffect(() => {
    if (user.role !== "superAdmin") {
      setServiceTypeFilter(user.outletID);
    }
  }, []);

  // Debounced company search per item
  useEffect(() => {
    // for each index with a query and no selected company, debounce and search
    Object.entries(companyQueries).forEach(([idx, query]) => {
      const index = Number(idx);
      if (selectedCompanies[index]?.id) return; // already selected

      if (companySearchTimers.current[index]) {
        clearTimeout(companySearchTimers.current[index]);
      }
      if (!query) {
        setCompanyResultsMap((m) => ({ ...m, [index]: [] }));
        setCompanyLoadingMap((m) => ({ ...m, [index]: false }));
        return;
      }
      setCompanyLoadingMap((m) => ({ ...m, [index]: true }));
      companySearchTimers.current[index] = setTimeout(() => {
        const q = (query || "").trim();
        const possibleId = /^\d+$/.test(q) ? Number(q) : undefined;
        getAllClients({ keyword: q, companyName: q, keyID: possibleId })
          .then((rows) => {
            const list = Array.isArray(rows) ? rows : [];
            const ql = q.toLowerCase();
            const companies = list
              .filter((r) => {
                const type = (r.entityType || r.type || "")
                  .toString()
                  .toLowerCase();
                const isCompany =
                  type === "company" || r.CompanyID || r.CompanyId;
                return !!isCompany;
              })
              .filter((r) => {
                const nm = (r.name || r.companyName || r.customerName || "")
                  .toString()
                  .toLowerCase();
                const em = (r.email || "").toString().toLowerCase();
                const rid = r.CompanyID || r.CompanyId || r.id;
                const idStr =
                  rid !== undefined && rid !== null ? String(rid) : "";
                return (
                  nm.includes(ql) ||
                  (!!em && em.includes(ql)) ||
                  (!!possibleId && Number(rid) === possibleId) ||
                  (!!idStr && idStr.includes(ql))
                );
              })
              .map((r) => ({
                id: r.CompanyID || r.CompanyId || r.id,
                name:
                  r.name ||
                  r.companyName ||
                  r.customerName ||
                  "Unnamed Company",
                email: r.email,
              }));
            setCompanyResultsMap((m) => ({ ...m, [index]: companies }));
          })
          .catch(() => setCompanyResultsMap((m) => ({ ...m, [index]: [] })))
          .finally(() =>
            setCompanyLoadingMap((m) => ({ ...m, [index]: false })),
          );
      }, 350);
    });
    // Cleanup function for useEffect
    return () => {
      Object.values(companySearchTimers.current || {}).forEach((t) =>
        clearTimeout(t),
      );
    };
  }, [companyQueries, selectedCompanies]);

  return (
    <div className="card">
      <form onSubmit={handleSubmit} noValidate>
        <div className="card-body">
          {/* Form Fields */}
          <div className="row gy-3 gx-2">
            <div className="col-md-10">
              <div className="row gy-3 gx-2">
                <div className="col-md-4">
                  <label htmlFor="type">
                    Type (Product/Service){" "}
                    <span className="text-danger">*</span>
                  </label>
                  <select
                    name="type"
                    value={form.type}
                    onChange={handleInputChange}
                    className={`form-select ${errors.type ? "is-invalid" : ""}`}
                    required
                    disabled={data}
                  >
                    <option value="">Select Type</option>
                    <option value="product">Product</option>
                    <option value="service">Service</option>
                  </select>
                  {errors.type && (
                    <div className="invalid-feedback">{errors.type}</div>
                  )}
                </div>
                {form.type === "product" && (
                  <>
                    <div className="col-md-4">
                      <label htmlFor="brand">Brand</label>
                      <div className="d-flex gap-1 align-items-center">
                        <select
                          name="brand"
                          value={form.brand}
                          onChange={handleInputChange}
                          className="form-select"
                        >
                          <option value="">Select Brand</option>
                          {/* Add brand options here */}
                          {brandsApi?.data?.data.map((elem, i) => {
                            return (
                              <option key={elem.brandID} value={elem.brandID}>
                                {elem.brandName}
                              </option>
                            );
                          })}
                        </select>
                        <button
                          type="button"
                          className="btn btn-outline-primary mb-0 btn-sm d-flex align-items-center"
                          style={{ textWrap: "nowrap", height: "40px" }}
                          onClick={() => setAddBrandModalShow(true)}
                        >
                          Add Brand
                        </button>
                      </div>
                    </div>
                  </>
                )}
                <div className="col-md-4">
                  <label htmlFor="name">
                    Product/Service Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleInputChange}
                    className={`form-control ${
                      errors.name ? "is-invalid" : ""
                    }`}
                    required
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <label htmlFor="selectIcon" className="d-block">
                Select Icon
              </label>
              {!form.iconString && (
                <button
                  type="button"
                  style={{ height: "40px" }}
                  onClick={() => setShowIconModal(true)}
                  className="btn btn-primary mb-0 btn-sm w-100"
                >
                  Select Icon
                </button>
              )}
              {errors.icon && <div className="text-danger">{errors.icon}</div>}
              {form.iconString && (
                <div
                  style={{
                    height: "40px",
                    width: "40px",
                    position: "relative",
                  }}
                >
                  <button
                    type="button"
                    className="badge bg-danger btn btn-danger mb-0 px-2 py-1"
                    title="Delete Image"
                    style={{
                      position: "absolute",
                      top: "7px",
                      left: "110%",
                      zIndex: "111",
                    }}
                    onClick={() =>
                      setForm({
                        ...form,
                        iconString: "",
                        icon: "",
                      })
                    }
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                  <img
                    src={form.iconString}
                    alt=""
                    className="img-thumbnail"
                    style={{
                      height: "40px",
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          {form.productDetails.length > 0 ? (
            form.productDetails.map((detail, index) => (
              <div className="border p-3 rounded-2 mt-3 mb-3" key={index}>
                <div className="d-flex justify-content-between mb-2">
                  <h5 className="mb-0 fs-6 fw-600 mb-0">Item {index + 1}</h5>
                  <i
                    className="fa fa-trash text-danger fs-5 cursor-pointer"
                    onClick={() => {
                      // If the row doesn't have a childId, it's a new UI row - delete without API call
                      const hasChildId = detail.childId || detail.ChildId;
                      if (!hasChildId) {
                        handleDeleteRow(index);
                      } else {
                        // Existing row with ID - use API delete
                        setSelectedRecord(detail);
                      }
                    }}
                  ></i>
                </div>
                <div className="row g-2">
                  <div className="col-md-4">
                    <label htmlFor="productServiceType">
                      Product/Service Specification
                    </label>
                    <select
                      name="type"
                      value={detail.type}
                      onChange={(e) => handleProductDetailsChange(index, e)}
                      className={`form-select ${
                        errors[`detail-type-${index}`] ? "is-invalid" : ""
                      }`}
                      required
                    >
                      <option value="">Select Type</option>
                      {serviceTypesApi?.data?.data.map((elem) => {
                        const isAllocatedElsewhere = allocatedTypeIds.has(
                          Number(elem.id),
                        );
                        const isUsedInCurrentForm = form.productDetails.some(
                          (d, i) =>
                            i !== index && Number(d.type) === Number(elem.id),
                        );
                        const isDisabled =
                          isAllocatedElsewhere || isUsedInCurrentForm;
                        return (
                          <option
                            key={elem.id}
                            value={elem.id}
                            disabled={isDisabled}
                            style={isDisabled ? { color: "red" } : {}}
                          >
                            {elem.serviceTypeName}
                            {isAllocatedElsewhere
                              ? " (Already Allocated)"
                              : isUsedInCurrentForm
                                ? " (Selected Above)"
                                : ""}
                          </option>
                        );
                      })}
                    </select>
                    {errors[`detail-type-${index}`] && (
                      <div className="invalid-feedback">
                        {errors[`detail-type-${index}`]}
                      </div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="code">Product/Service Code</label>
                    <input
                      type="text"
                      name="code"
                      value={detail.code || ""}
                      onChange={(e) => handleProductDetailsChange(index, e)}
                      className={`form-control ${
                        errors[`detail-code-${index}`] ? "is-invalid" : ""
                      }`}
                      required
                      disabled={true} // Disable field to prevent tampering
                      style={{
                        backgroundColor: "#f8f9fa",
                        cursor: "not-allowed",
                      }}
                    />
                    {errors[`detail-code-${index}`] && (
                      <div className="invalid-feedback">
                        {errors[`detail-code-${index}`]}
                      </div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="description">Description</label>
                    <input
                      type="text"
                      name="description"
                      value={detail.description || ""}
                      onChange={(e) => handleProductDetailsChange(index, e)}
                      className={`form-control ${
                        errors[`detail-description-${index}`]
                          ? "is-invalid"
                          : ""
                      }`}
                      required
                    />
                    {errors[`detail-description-${index}`] && (
                      <div className="invalid-feedback">
                        {errors[`detail-description-${index}`]}
                      </div>
                    )}
                  </div>
                  {form.type === "product" && ( // Only show this for products
                    <>
                      <div className="col-md-4">
                        <label htmlFor="reOrderLevel">Re-Order Level</label>
                        <input
                          type="number"
                          name="reOrderLevel"
                          value={detail.reOrderLevel ?? ""}
                          onChange={(e) => handleProductDetailsChange(index, e)}
                          className={`form-control ${
                            errors[`detail-reOrderLevel-${index}`]
                              ? "is-invalid"
                              : ""
                          }`}
                          required
                        />
                        {errors[`detail-reOrderLevel-${index}`] && (
                          <div className="invalid-feedback">
                            {errors[`detail-reOrderLevel-${index}`]}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <label htmlFor="quantity">Quantity</label>
                        <input
                          type="number"
                          name="quantity"
                          value={detail.quantity ?? ""}
                          onChange={(e) => handleProductDetailsChange(index, e)}
                          className={`form-control ${
                            errors[`detail-quantity-${index}`]
                              ? "is-invalid"
                              : ""
                          }`}
                          required
                        />
                        {errors[`detail-quantity-${index}`] && (
                          <div className="invalid-feedback">
                            {errors[`detail-quantity-${index}`]}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <label htmlFor="price">Cost Price</label>
                        <input
                          type="number"
                          name="costPrice"
                          value={detail.costPrice ?? ""}
                          onChange={(e) => handleProductDetailsChange(index, e)}
                          className={`form-control ${
                            errors[`detail-costPrice-${index}`]
                              ? "is-invalid"
                              : ""
                          }`}
                          required
                        />
                        {errors[`detail-costPrice-${index}`] && (
                          <div className="invalid-feedback">
                            {errors[`detail-costPrice-${index}`]}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <label htmlFor="rate">Rate</label>
                        <input
                          type="number"
                          name="rate"
                          value={detail.rate ?? ""}
                          onChange={(e) => handleProductDetailsChange(index, e)}
                          className={`form-control ${
                            errors[`detail-rate-${index}`] ? "is-invalid" : ""
                          }`}
                          required
                        />
                        {errors[`detail-rate-${index}`] && (
                          <div className="invalid-feedback">
                            {errors[`detail-rate-${index}`]}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {form.type === "service" && ( // Only show this for services
                    <div className="col-md-4">
                      <label htmlFor="rate">Rate</label>
                      <input
                        type="number"
                        name="rate"
                        value={detail.rate ?? ""}
                        onChange={(e) => handleProductDetailsChange(index, e)}
                        className={`form-control ${
                          errors[`detail-rate-${index}`] ? "is-invalid" : ""
                        }`}
                        required
                      />
                      {errors[`detail-rate-${index}`] && (
                        <div className="invalid-feedback">
                          {errors[`detail-rate-${index}`]}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Optional Company Name search per item */}
                  <div className="col-md-4">
                    <label>Company Name (optional)</label>
                    <div className="position-relative">
                      <input
                        type="text"
                        autoComplete="off"
                        placeholder="Search company..."
                        value={
                          typeof detail.clientName === "string" &&
                          detail.clientName.trim() !== ""
                            ? detail.clientName
                            : (selectedCompanies[index]?.name ??
                              companyQueries[index] ??
                              "")
                        }
                        onChange={(e) => {
                          setSelectedCompanies((m) => ({
                            ...m,
                            [index]: null,
                          }));
                          setCompanyQueries((m) => ({
                            ...m,
                            [index]: e.target.value,
                          }));
                        }}
                        className="form-control"
                      />
                      {(selectedCompanies[index] || companyQueries[index]) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-link position-absolute"
                          style={{ right: 6, top: 4 }}
                          onClick={() => {
                            setSelectedCompanies((m) => ({
                              ...m,
                              [index]: null,
                            }));
                            setCompanyQueries((m) => ({ ...m, [index]: "" }));
                            setCompanyResultsMap((m) => ({
                              ...m,
                              [index]: [],
                            }));
                          }}
                          title="Clear"
                        >
                          ✕
                        </button>
                      )}
                      {Array.isArray(companyResultsMap[index]) &&
                        companyResultsMap[index].length > 0 &&
                        !selectedCompanies[index] &&
                        companyQueries[index] && (
                          <div
                            className="list-group"
                            style={{
                              position: "absolute",
                              zIndex: 1000,
                              width: "100%",
                              maxHeight: 220,
                              overflowY: "auto",
                            }}
                          >
                            {companyResultsMap[index].map((c) => (
                              <button
                                type="button"
                                key={c.id}
                                className="list-group-item list-group-item-action"
                                onClick={() => {
                                  setSelectedCompanies((m) => ({
                                    ...m,
                                    [index]: c,
                                  }));
                                  setCompanyQueries((m) => ({
                                    ...m,
                                    [index]: "",
                                  }));
                                  setCompanyResultsMap((m) => ({
                                    ...m,
                                    [index]: [],
                                  }));
                                }}
                              >
                                {c.name} {c.email ? `· ${c.email}` : ""}
                              </button>
                            ))}
                            {companyLoadingMap[index] && (
                              <div className="list-group-item text-muted">
                                Searching…
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                    {selectedCompanies[index] && (
                      <small className="text-success">
                        Selected: {selectedCompanies[index].name}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="border p-3 rounded mt-3 mb-3">
              <h6 className="text-center mb-0">No Product/Service available</h6>
            </div>
          )}
          <div className="text-center">
            <button
              type="button"
              className="btn badge badge-lg badge-success fw-600 mb-0"
              onClick={addMoreRows}
            >
              Add More Product/Service{" "}
            </button>
          </div>
        </div>

        <div className="card-footer d-flex align-items-center justify-content-between">
          {/* Is Active Switch */}
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="isActive">
              Is Active ?
            </label>
          </div>

          {/* Submit Button */}
          <button type="submit" className="btn btn-primary mb-0">
            Submit
          </button>
        </div>
      </form>
      {/* Icon Selection Modal */}
      <Modal show={showIconModal} onHide={() => setShowIconModal(false)}>
        <Modal.Body className="p-4">
          <h5 className="mb-3">Select Icon</h5>
          <IconSelector
            handleIconSelect={handleIconSelect}
            iconId={form?.icon}
          />
          <div className="mt-3 text-center">
            <button
              type="button"
              className="bg-danger btn btn-danger mb-0 btn-sm"
              onClick={() => setShowIconModal(false)}
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal For Add Product */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Body className="text-center p-4">
          <h5>Are you sure you want to delete this row?</h5>
          <p className="text-sm mb-4">
            This action will permanently delete the row.
          </p>
          <div className="buttons d-flex gap-3 justify-content-center">
            <button
              className="btn btn-danger bg-danger btn-sm mb-0"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm mb-0"
              onClick={confirmDeleteRow}
            >
              Confirm
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* DELETE MODAL For Edit Product */}
      <Modal
        show={selectedRecord}
        onHide={() => setSelectedRecord(null)}
        size="sm"
        centered
      >
        <Modal.Body className="text-center p-4">
          <h5>Are you sure you want to delete this row?</h5>
          <p className="text-sm mb-4">
            This action will permanently delete the row.
          </p>
          <div className="buttons d-flex gap-3 justify-content-center">
            <button
              className="btn btn-danger bg-danger btn-sm mb-0"
              onClick={() => setSelectedRecord(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm mb-0"
              onClick={handleDeleteServiceProductChild}
            >
              Confirm
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <AddBrand />
    </div>
  );
};

export default AddEditServiceProduct;
